/**
 * @module project/components/entry_details_header
 */
import React, { PropTypes, Component } from 'react';
import { Link } from 'react-router';
import classnames from 'classnames';
import Tooltip from 'react-tooltip';
import moment from 'moment';
import partial from 'lodash/partial';
import StarRating from 'common/components/star_rating';
import DropdownMenu from 'common/components/dropdown_menu';
import SingleEntryHeader from 'common/components/modal/single_entry_header';
import Revisions from 'project/components/entry_details/revisions';
import Icon from 'common/components/svg_icon';
import { isMobile } from 'common/utils';
import { ADMIN_ROOT } from 'common/variables';

/**
 * Header component for the project single entry modal.
 *
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
export default class EntryDetailsHeader extends Component {
  constructor(props) {
    super(props);
    this.toggleDelete = this.toggleDelete.bind(this);
    this.toggleReinstate = this.toggleReinstate.bind(this);
    this.renderDropdownMenu = this.renderDropdownMenu.bind(this);
    this.toggleRemoveCreative = this.toggleRemoveCreative.bind(this);
    this.toggleViolation = this.toggleViolation.bind(this);
  }

  /*
   * Prepare data to render top header element
   */
  getTopHeader() {
    const { entry, revision, pageNumber, itemsCount, permissions, user,
      onNextPage, onPrevPage, onToggleEntryDetails } = this.props;
    const leftElement = (
      <span className="entry-title">
        #{entry.entry_number}
        {' '}by <Link target="_blank" to={
          user.is_staff? `${ADMIN_ROOT}/creative/${entry.author}/` : `/users/${entry.author_username}`
        }>{entry.author_username}</Link> {moment(revision.created_at).fromNow()}
      </span>
    );

    const params = {
      leftElement,
      pageNumber,
      itemsCount,
      onNextPage,
      onPrevPage,
      hidePager: !permissions.canViewDetailsPager,
      onToggleDetails: onToggleEntryDetails,
    };

    return params;
  }

  /*
   * Prepare data to render bottom header element
   */
  getBottomHeader() {
    const {
      comments, entry, permissions, revision, onToggleLeftDrawer, onRateClick, detailsState,
      onChangeRevision
    } = this.props;
    const leftControls = [];
    let centerElement = null;
    let rater = null;

    if (permissions.canViewRevisions && entry.revisions.findIndex(r => r.id === revision.id) > 0) {
      centerElement = (
        <div className="seeing-older-rev show-for-large">
          This is an older version.{' '}
          <button className="btn btn-text btn-md" onClick={() => onChangeRevision(entry.revisions[0].id)}>
            View most recent.
          </button>
        </div>
      );
    }

    if (permissions.canViewRevisions) {
      leftControls.push(
        <Revisions
          key="revisions"
          entry={entry}
          currentRevision={detailsState.currentRevision}
          onRevisionChange={onChangeRevision}
        />
      );
    }

    if (permissions.canComment) {
      leftControls.push(
        <div key="comments_toggle"
         className={classnames("comments-toggle", { closed: !detailsState.entryCommentsVisible })}
         onClick={() => onToggleLeftDrawer('comments')}>
          <i className={"fa fa-angle-double-" + (detailsState.entryCommentsVisible? "left" : "right")}
            data-tip={true} data-for="tooltip-comments-toggle"
            data-event={isMobile()? "click" : undefined}/>
          <Tooltip class="tooltip" effect="solid" place="right"
              id="tooltip-comments-toggle">
            { detailsState.entryCommentsVisible? "Hide comments" : "Show comments" }
          </Tooltip>
        </div>
      );
    }

    if (permissions.canViewScore) {
      rater = (
        <StarRating
          key={revision.id}
          min={1}
          readonly={!permissions.canRate}
          initialRate={revision.score || 0}
          onChange={(sc) => {
            onRateClick(revision.id, sc);
          }}
          extraClasses="xxl"
        />
      );
    }

    return {
      leftControls,
      centerElement,
      rater,
    };
  }

  toggleRemoveCreative() {
    const { entry, toggleRemoveCreativeModal } = this.props;
    toggleRemoveCreativeModal({ id: entry.author, username: entry.author_username });
  }

  toggleDelete() {
    const { entry, toggleEntryDelete } = this.props;
    toggleEntryDelete(entry.id);
  }

  toggleReinstate() {
    const { entry, toggleEntryReinstate } = this.props;
    toggleEntryReinstate(entry.id);
  }

  toggleViolation() {
    const { entry, params: { subcategory, pageSlug }, toggleViolationModal } = this.props;
    const link = (
      window.location.origin + `/${subcategory}/${pageSlug}/entry/${entry.id}/`
    );
    toggleViolationModal({ type: 'entry', id: entry.id, link: link });
  }

  renderDropdownMenu() {
    const { onToggleLeftDrawer, permissions, entry, displayedVersion } = this.props;
    const version = `Version: ${displayedVersion}`;

    const buttons = [{
      label: <span><Icon className="svg-icon inline" icon="trash" /> Eliminate entry</span>,
      action: this.toggleDelete,
      show: permissions.canEliminate,
    }, {
      label: <span><Icon className="svg-icon inline" icon="trash" /> Withdraw entry</span>,
      action: this.toggleDelete,
      show: permissions.canWithdraw,
    }, {
      label: <span><i className="fa fa-undo"/> Un-withdraw entry</span>,
      action: this.toggleReinstate,
      show: permissions.canUnwithdraw,
    }, {
      label: <span><i className="fa fa-undo"/> Un-eliminate entry</span>,
      action: this.toggleReinstate,
      show: permissions.canUneliminate,
    },/* FIXME: IMPLEMENT THIS {
      extraClasses: 'show-for-small-only',
      label: <span><i className="fa fa-circle" aria-hidden="true" /> Show Hotspots</span>,
      action: () => {},
      show: entry.entry_type === 'image' && permissions.canComment,
    }, */ {
      extraClasses: 'hide-for-large',
      action: partial(onToggleLeftDrawer, 'comments'),
      label: <span><Icon className="svg-icon inline" icon="comments" /> Comments</span>,
      show: permissions.canComment,
    }, {
      extraClasses: 'hide-for-large',
      label: <span><Icon className="svg-icon inline" icon="versions" />{version}</span>,
      action: partial(onToggleLeftDrawer, 'revisions'),
      show: permissions.canViewRevisions,
    }, {
      label: <span><i className="fa fa-user-times" aria-hidden="true" />Remove creative</span>,
      show: permissions.canRemoveCreative,
      action: this.toggleRemoveCreative,
    }, {
      label: <span><i className="fa fa-ban" aria-hidden="true" /> Report violation</span>,
      show: permissions.canReportViolation,
      action: this.toggleViolation
    }];

    return (<DropdownMenu buttons={buttons} className="right" />);
  }

  render() {
    const topHeaderData = this.getTopHeader();
    const bottomHeaderData = this.getBottomHeader();

    return (
      <SingleEntryHeader
        topHeaderData={topHeaderData}
        bottomHeaderData={bottomHeaderData}
        dropdownData={null}
        renderDropdownMenu={this.renderDropdownMenu}
        {...this.props}
      />
    );
  }
}

EntryDetailsHeader.propTypes = {
  permissions: PropTypes.object,
  project: PropTypes.object,
  user: PropTypes.object,
  params: PropTypes.object,
  entry: PropTypes.object,
  revision: PropTypes.object,
  comments: PropTypes.array,
  pageNumber: PropTypes.number,
  itemsCount: PropTypes.number,
  displayedVersion: PropTypes.string,
  onToggleEntryDetails: PropTypes.func,
  onToggleLeftDrawer: PropTypes.func,
  onRateClick: PropTypes.func,
  onPrevPage: PropTypes.func,
  onNextPage: PropTypes.func,
  toggleRemoveCreativeModal: PropTypes.func,
  toggleEntryDelete: PropTypes.func,
  toggleEntryReinstate: PropTypes.func,
  detailsState: PropTypes.object,
};



// WEBPACK FOOTER //
// ./project/components/entry_details/entry_details_header.jsx